import {
  Box,
  ButtonGroup,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  FrameworkAgreementType,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { FRAMEWORK_AGREEMENT_OPTIONS, isFrameworkEnabled } from "../utils/publicProcurementOptions";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { FieldSaveError } from "./FieldSaveError";

interface Props {
  eventId: string;
}

export const FrameworkAgreementField = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;

  const currentType = publicContext?.frameworkAgreement ?? FrameworkAgreementType.None;
  const enabled = isFrameworkEnabled(currentType);
  const maxParticipants = publicContext?.frameworkMaxParticipants ?? null;
  const saving = fieldStatus("publicContext.frameworkAgreement");
  const isSaving = saving === "saving" || saving === "pending";
  const isSavingMax =
    fieldStatus("publicContext.frameworkMaxParticipants") === "saving" ||
    fieldStatus("publicContext.frameworkMaxParticipants") === "pending";

  const handleToggle = (checked: boolean) => {
    const defaultType = FRAMEWORK_AGREEMENT_OPTIONS[0].value;
    updateField({
      publicContext: {
        frameworkAgreement: checked ? defaultType : FrameworkAgreementType.None,
        frameworkMaxParticipants: checked ? maxParticipants : null,
      },
    });
  };

  const handleTypeChange = (value: string) => {
    updateField({ publicContext: { frameworkAgreement: value as FrameworkAgreementType } });
  };

  const handleMaxParticipantsChange = (value: number | null) => {
    updateField({ publicContext: { frameworkMaxParticipants: value } }, { debounceMs: 300 });
  };

  return (
    <FormControl>
      <Flex align="center" justify="space-between" gap="4" mb={enabled ? "4" : "0"}>
        <FormLabel mb="0">{t("Framework agreement")}</FormLabel>
        <Flex align="center" gap="2">
          {isSaving && <Spinner size="xs" />}
          <ButtonGroup isAttached size="sm">
            <Button
              colorScheme={enabled ? "teal" : "gray"}
              variant={enabled ? "solid" : "outline"}
              onClick={() => handleToggle(true)}
            >
              {t("Yes")}
            </Button>
            <Button
              colorScheme={!enabled ? "teal" : "gray"}
              variant={!enabled ? "solid" : "outline"}
              onClick={() => handleToggle(false)}
            >
              {t("No")}
            </Button>
          </ButtonGroup>
        </Flex>
      </Flex>
      <FieldSaveError eventId={eventId} path="publicContext.frameworkAgreement" />

      {enabled && (
        <Stack spacing="5">
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="smText" mb="2">
              {t("Call-off mechanism")}
            </Text>
            <RadioOptionGroup
              options={FRAMEWORK_AGREEMENT_OPTIONS}
              value={currentType}
              onChange={handleTypeChange}
            />
          </Box>
          <Box>
            <Flex align="center" gap="2" mb="2">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {t("Number of parties (suppliers)")}
              </Text>
              {isSavingMax && <Spinner size="xs" />}
            </Flex>
            <FieldSaveError eventId={eventId} path="publicContext.frameworkMaxParticipants" />
            <NumericInput
              numericValue={maxParticipants}
              onNumericChange={handleMaxParticipantsChange}
              min={1}
              maxW={{ base: "full", sm: "200px" }}
            />
          </Box>
        </Stack>
      )}
    </FormControl>
  );
};
